import { z } from "zod";

/**
 * Contrat des mesures et de la fausse porte (spec docs/idees/concerts/05-spec.md, sections 3.3,
 * 4 et 6 ; ticket 4, issue #316). Partage par le backend, qui valide chaque corps recu, et par
 * le client du navigateur (`packages/frontend/src/app/api.ts`), qui valide avant d'envoyer.
 *
 * Aucune donnee de la liste de groupes ne quitte le navigateur, sauf leur nombre. La seule
 * donnee personnelle est l'adresse laissee sur la liste d'attente : elle va en base, jamais dans
 * un evenement ni dans un journal.
 */

/** Les trois prix de la fausse porte, en centimes d'euro. Rien n'est vendu pendant le test. */
export const PRIX_EN_CENTIMES = {
  "fichier-hd": 790,
  annuel: 1200,
  "a-vie": 2900,
} as const;

export const PRODUITS = ["fichier-hd", "annuel", "a-vie"] as const;
export type Produit = (typeof PRODUITS)[number];
export const produitSchema = z.enum(PRODUITS);

/** Une activation se pose au dixieme groupe colle : en dessous, le serveur la refuse. */
export const SEUIL_ACTIVATION = 10;

/** Plafond de la saisie cote navigateur (`parse.ts`, 300 groupes au plus). */
export const GROUPES_MAX = 300;

export const evenementSchema = z.discriminatedUnion("type", [
  /** La liste atteint dix groupes : posee une seule fois par navigateur. */
  z.strictObject({
    type: z.literal("activation"),
    count: z.int().min(SEUIL_ACTIVATION).max(GROUPES_MAX),
  }),
  /** Clic sur un prix : ouvre la fausse porte. */
  z.strictObject({ type: z.literal("checkout"), product: produitSchema }),
]);
export type Evenement = z.infer<typeof evenementSchema>;

/**
 * Inscription sur la liste d'attente. `consent` doit valoir `true` (case non cochee par
 * defaut) ; `piege` est le champ anti-robot, cache a l'humain : rempli, la demande est ignoree
 * sans le dire. Le schema rend l'adresse sans blancs et en minuscules, avant de la valider.
 */
export const listeAttenteSchema = z.strictObject({
  // Normalisee avant d'etre validee : " Nico@Exemple.FR " passe et arrive en minuscules.
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  product: produitSchema,
  consent: z.literal(true),
  piege: z.string().max(200).optional(),
});
export type ListeAttente = z.infer<typeof listeAttenteSchema>;

/**
 * Reponse de `POST /api/liste-attente`. La meme pour une adresse neuve, deja inscrite ou une
 * demande piegee : la page ne doit pas permettre de savoir qui est sur la liste.
 */
export const listeAttenteReponseSchema = z.object({ ok: z.literal(true) });
export type ListeAttenteReponse = z.infer<typeof listeAttenteReponseSchema>;

/** Adresse telle qu'elle est rangee : sans blancs autour, en minuscules. */
export function normaliserEmail(email: string): string {
  return email.trim().toLowerCase();
}
